import React, { type ReactElement, useMemo } from "react";
import { StyleSheet, View } from "react-native";

import { Error } from "../icons";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";

export interface ErrorTextProps {
  /**
   * The error message to display
   */
  children: string;

  /**
   * Unique React Native ID used to associate with the form field to its error text for A11y
   */
  nativeID?: string;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * **Accessibility label for the error icon.** This is read by screen readers to describe the icon's meaning.
   * It should typically be set to a short phrase like **'Error'** or **'Critical'**.
   */
  errorIconAccessibilityLabel?: string;
}

/**
 * Displays an error message with an error icon
 */
export function ErrorText({
  children,
  nativeID,
  sx,
  errorIconAccessibilityLabel,
}: ErrorTextProps): ReactElement {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: "row",
          alignItems: "flex-start",
          gap: theme.spacing.s1,
        },
        errorText: {
          color: theme.colors.foregroundStatusCritical,
        },
      }),
    [theme],
  );

  return (
    <View style={[styles.container, sx]}>
      <Error
        title={errorIconAccessibilityLabel}
        testID="error-icon"
        color={theme.colors.foregroundStatusCritical}
        size={"sm"}
        aria-hidden={!errorIconAccessibilityLabel}
      />
      <Text level={2} sx={styles.errorText} nativeID={nativeID}>
        {children}
      </Text>
    </View>
  );
}
